import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { webApi } from '../services/api';
import { isCanceledRequest } from '../utils/abortRequest';
import { StatusBanner } from '../components/ui';
import { ReviewCard } from '../components/reviews/ReviewCard';
import { IcArrowBack, IcEmptyListIllustration } from '../components/reviews/EmployerReviewIcons';

/**
 * Employer “all reviews” screen — matches Android EmployerReviewActivity.
 * API period values: today, week, month, custom (+ startDate, endDate as YYYY-MM-DD).
 */
const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'custom', label: 'Custom' }
];

function formatRangeSummary(startIso, endIso) {
  const fmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const start = new Date(`${startIso}T12:00:00`);
  const end = new Date(`${endIso}T12:00:00`);
  if (startIso === endIso) return fmt.format(start);
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

function ReviewsPage() {
  const [query, setQuery] = useState({ period: 'today' });
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customSummary, setCustomSummary] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (query.period === 'custom' && (!query.startDate || !query.endDate)) {
      setLoading(false);
      return undefined;
    }

    const ac = new AbortController();
    const { signal } = ac;
    (async () => {
      try {
        setLoading(true);
        const res = await webApi.getAllReviews(query, { signal });
        if (signal.aborted) return;
        setData(res || { averageRating: 0, list: [] });
        setError('');
      } catch (err) {
        if (signal.aborted || isCanceledRequest(err)) return;
        setError(err.message || 'Failed to load reviews');
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [query]);

  const list = Array.isArray(data?.list) ? data.list : [];

  const onPeriodClick = (value) => {
    if (value === 'custom') {
      setQuery({ period: 'custom' });
      return;
    }
    setCustomSummary('');
    setQuery({ period: value });
  };

  const applyCustomRange = () => {
    if (!customStart || !customEnd) {
      setError('Choose start and end dates');
      return;
    }
    if (customEnd < customStart) {
      setError('End date must be on or after start date');
      return;
    }
    setError('');
    setCustomSummary(formatRangeSummary(customStart, customEnd));
    setQuery({ period: 'custom', startDate: customStart, endDate: customEnd });
  };

  return (
    <div className="reviews-list-page">
      <div className="reviews-list-fixed-head">
        <header className="reviews-list-appbar">
          <Link to="/report" className="reviews-list-back" aria-label="Back">
            <IcArrowBack />
          </Link>
          <h1 className="reviews-list-title">Reviews</h1>
          <span className="reviews-list-appbar-spacer" aria-hidden="true" />
        </header>

        <div className="reviews-list-filters" role="tablist" aria-label="Review period">
          {PERIODS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={query.period === value}
              className={`reviews-list-filter${query.period === value ? ' reviews-list-filter--selected' : ''}`}
              onClick={() => onPeriodClick(value)}
            >
              {label}
            </button>
          ))}
        </div>

        {query.period === 'custom' && (
          <div className="reviews-list-custom-range">
            <label className="reviews-list-custom-field">
              <span>From</span>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
            </label>
            <label className="reviews-list-custom-field">
              <span>To</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
            </label>
            <button type="button" className="reviews-list-custom-apply" onClick={applyCustomRange}>
              Apply
            </button>
          </div>
        )}

        {customSummary && query.period === 'custom' && (
          <p className="reviews-list-custom-summary">
            Showing reviews for <strong>{customSummary}</strong>
          </p>
        )}
      </div>

      <div className="reviews-list-content">
        <StatusBanner kind="error" message={error} />

        {loading ? (
          <p className="reviews-list-loading muted">Loading reviews...</p>
        ) : list.length === 0 ? (
          <div className="reviews-list-empty">
            <IcEmptyListIllustration className="reviews-list-empty-icon" />
            <p className="reviews-list-empty-title">No Review Found</p>
            <p className="reviews-list-empty-sub">No Reviews are available for the selected period</p>
          </div>
        ) : (
          <div className="reviews-list-cards">
            {list.map((review, idx) => (
              <ReviewCard
                key={`${review.createdTime || ''}-${idx}-${review.name || ''}`}
                review={review}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ReviewsPage;
